import {
  BillingTerm,
  IndividualPlanCode,
  paddleIndividualItemsProd,
  paddleIndividualItemsSandbox,
} from '@easy-expense/utils-shared';

import { SETTINGS_BILLING_CHECKOUT_COMPLETE_ROUTE, SETTINGS_BILLING_ROUTE } from './constants';

/**
 * Unauthenticated ee-web route that opens a Paddle checkout for an individual plan from query
 * params alone, the way `/checkout/team` does for Team. Unauthenticated for the same reason --
 * the purchase binds to the `userID` in the link, not to a session -- even though every entry
 * point that reaches it today is inside ee-web and has one.
 */
export const INDIVIDUAL_CHECKOUT_PATH = '/checkout/individual';

/**
 * Checkout Source: where an Individual Checkout was opened from. Only ee-web sells these plans
 * through Paddle -- the mobile app sells them through the app stores -- so there is one value
 * today, and the type exists so a second entry point has somewhere to declare itself rather than
 * being folded into this one and becoming unmeasurable.
 */
export type IndividualCheckoutSource = 'web_billing';

const CHECKOUT_SOURCES: IndividualCheckoutSource[] = ['web_billing'];
const BILLING_TERMS: BillingTerm[] = ['monthly', 'annual'];
const INDIVIDUAL_PLANS: IndividualPlanCode[] = ['STARTER', 'PROFESSIONAL'];

export type IndividualCheckoutParams = {
  userID: string;
  plan: IndividualPlanCode;
  term: BillingTerm;
  email: string;
  source: IndividualCheckoutSource;
  /**
   * Scopes the checkout's billing lookup to one workspace, so Trial Eligibility is resolved
   * against the same organization the buyer was looking at.
   *
   * Safe to carry in a URL -- the callable behind it takes identity from the caller's auth token
   * and rejects a workspace the caller is not a member of, so a hand-edited value buys nothing.
   */
  workspaceID?: string;
};

export type IndividualCheckoutRequest = {
  userID: string;
  plan: IndividualPlanCode;
  term: BillingTerm;
  email: string;
  source: IndividualCheckoutSource | null;
  workspaceID: string | null;
};

/** Where the buyer goes when checkout completes, and when they back out of it. */
export type IndividualCheckoutDestinations = {
  success: string;
  cancel: string;
};

/**
 * Builds the query string for {@link INDIVIDUAL_CHECKOUT_PATH}. Carries the plan but never a
 * price ID: the route resolves the price itself from the plan and term, which is what stops a
 * client from ever opening checkout on a deprecated or legacy price.
 */
export function buildIndividualCheckoutQuery(params: IndividualCheckoutParams): string {
  const query = new URLSearchParams({
    userID: params.userID,
    plan: params.plan,
    term: params.term,
    email: params.email,
    source: params.source,
  });
  if (params.workspaceID) {
    query.set('workspaceID', params.workspaceID);
  }
  return query.toString();
}

/**
 * Reads an Individual Checkout request off its query string. Returns null when the request
 * cannot say who is buying or what they are buying:
 *
 * - no `userID`, because a checkout without one creates a Paddle subscription the webhook can
 *   only attach by email lookup, which is the fallback path, not the contract;
 * - no recognisable `plan`, because Starter and Professional are different products at different
 *   prices and there is no safe default -- guessing sells the wrong one.
 *
 * A missing or unknown `term` falls back to monthly, which is the cheaper commitment of the two
 * and the one every entry point defaults to.
 */
export function parseIndividualCheckoutRequest(
  params: URLSearchParams,
): IndividualCheckoutRequest | null {
  const userID = params.get('userID');
  const planParam = params.get('plan') as IndividualPlanCode | null;
  if (!userID || !planParam || !INDIVIDUAL_PLANS.includes(planParam)) {
    return null;
  }
  const termParam = params.get('term') as BillingTerm | null;
  const term = termParam && BILLING_TERMS.includes(termParam) ? termParam : 'monthly';
  const sourceParam = params.get('source') as IndividualCheckoutSource | null;
  const source = sourceParam && CHECKOUT_SOURCES.includes(sourceParam) ? sourceParam : null;

  return {
    userID,
    plan: planParam,
    term,
    email: params.get('email') ?? '',
    source,
    workspaceID: params.get('workspaceID'),
  };
}

/**
 * Where this checkout returns its buyer. Every individual plan is bought from Billing settings
 * and belongs to a signed-in session, so unlike the Team Checkout -- which must also serve a tab
 * the mobile app opened fresh, with nothing behind it -- there is one destination regardless of
 * source. The success route carries the marker that makes Billing poll for the new subscription
 * rather than show the paywall the buyer just paid to leave.
 */
export function individualCheckoutDestinations(): IndividualCheckoutDestinations {
  return {
    success: SETTINGS_BILLING_CHECKOUT_COMPLETE_ROUTE,
    cancel: SETTINGS_BILLING_ROUTE,
  };
}

/**
 * The one price an Individual Checkout may sell: the current price for the requested plan and
 * term. Prod/sandbox is decided by the build, never by hostname.
 *
 * `trialEligible` is the caller's answer to Trial Eligibility, never the URL's -- a buyer who has
 * subscribed before does not earn a second trial by cancelling and coming back.
 */
export function resolveIndividualCheckoutPriceID(
  plan: IndividualPlanCode,
  term: BillingTerm,
  isProduction: boolean,
  trialEligible: boolean,
): string {
  const items = isProduction ? paddleIndividualItemsProd : paddleIndividualItemsSandbox;
  const variant = items[plan][term];
  return trialEligible ? variant.trial : variant.noTrial;
}
